/**
 * The default colorway for a variant -- Jake's resolution ladder.
 *
 * Jake, 2026-09-02 (product editor, placements page): "when a variant is
 * created, it should default to the first colorway that matches the color
 * routes -- all colors, light garments, dark garments. A new placement
 * defaults to an all-colors variant, so use the first all-colors colorway if
 * there is one, otherwise pick the first available colorway. If there are no
 * colorways, use the all-black version that we see now."
 *
 * A PURE FUNCTION, deliberately in its own module rather than a computed
 * inside VariantCard. Three reasons: the same ladder has to be reachable
 * from wherever a variant is CREATED (not only where a card is rendered), it
 * is the kind of rule that wants exhaustive unit tests without mounting a
 * 2000-line component, and keeping it in one place is what stops a second,
 * subtly-different copy appearing next to it.
 *
 * SHARPENED THE NEXT DAY (Jake, 2026-09-02, same surface): "the default
 * colorway picked for light garment or dark garment should be the one whose
 * contrast against the substrate is most appropriate." Rung 1 still means
 * "declared for this colour route"; what changed is how it breaks a
 * MULTI-WAY match -- best real ink-vs-garment contrast rather than lowest
 * id. Rungs 2 and 3 are untouched, and so is the all-colours route, which
 * covers a MIX of shades and therefore has no one substrate to be
 * appropriate against.
 *
 * WHY LOWEST id IS STILL THE TIE-BREAK. Jake's item 2 established that
 * several colorways may share a substrate target (a monochrome dark and a
 * full-colour dark are both legitimate), so the ladder needs a total order
 * -- and one that does NOT move when a colorway is renamed or re-saved, or a
 * variant's default would silently change under the user. Contrast is
 * computed from thread and garment hexes, which a rename does not touch, so
 * ranking by it PRESERVES that property; and two colorways can score
 * equally (trivially: the same threads saved twice), where something still
 * has to decide. The two rules compose rather than compete.
 *
 *   * `id` is IMMUTABLE, which is the whole property this relies on -- and
 *     it is verified, not assumed: `artwork_colorways.id` is
 *     `integer NOT NULL DEFAULT nextval('artwork_colorways_id_seq')`, and
 *     nothing in the API ever assigns to `colorway.id` (the update handler
 *     assigns `.name` and `.substrate_target` and nothing else). A
 *     replace-by-name is a PUT onto the SAME row, so re-saving 'navy' as
 *     'Navy' keeps the id and cannot reorder anything.
 *   * `name` is precisely the failure mode Jake warned about.
 *   * creation order is not separately stored, and `created_at` is not
 *     unique, so it cannot break ties by itself.
 *
 * NOTE the requirement is STABILITY, not monotonicity. A sequence can skip
 * (a rolled-back INSERT burns a value) and ids need not be gap-free or
 * even globally ordered by creation time. None of that matters here: the
 * ladder needs a TOTAL ORDER that does not change under rename or re-save,
 * and an immutable integer PK gives exactly that. Claiming monotonicity
 * would be claiming more than the schema guarantees.
 *
 * The list endpoint sorts by NAME because that is display order. This
 * resolver sorts by id ITSELF rather than trusting incoming order, so
 * display order and resolution order cannot drift apart.
 */

import {
    VARIANT_KIND_CATCH_ALL_DARK,
    VARIANT_KIND_CATCH_ALL_LIGHT,
    VARIANT_KIND_SINGLE_CATCH_ALL,
} from '@/utils/decorationRouting'
import { readabilityFor } from '@/utils/artworkReadability'

// Which declared substrate a variant's colour route implies.
//
// Only the two SHADE routes imply one. `single_catch_all` (all colours) and
// `specific_colors` (a named SKU set) do not map to 'light' or 'dark' -- and
// for the all-colours route that is not an absence but a POSITIVE match
// against `substrate_target IS NULL`, which is how "applies to all" is
// stored (see ArtworkColorway: NULL is the value, not a missing value).
const KIND_TO_SUBSTRATE = Object.freeze({
    [VARIANT_KIND_CATCH_ALL_LIGHT]: 'light',
    [VARIANT_KIND_CATCH_ALL_DARK]: 'dark',
})

// The all-colours route matches colorways declared for all substrates,
// which are stored as NULL. Kept as a named export so a caller can express
// "this route wants the all-substrates colorways" without re-deriving it.
export const ALL_SUBSTRATES = null

/**
 * Which substrate this variant kind implies, or ALL_SUBSTRATES.
 *
 * `single_catch_all` returns ALL_SUBSTRATES (null) as a real answer: the
 * variant covers every colour, so the colorway that also covers every
 * colour is its match. `specific_colors` returns `undefined` -- a genuinely
 * different thing, meaning "no route-implied substrate at all", which sends
 * the ladder straight to its fallback rung.
 */
export function impliedSubstrateForKind(kind) {
    if (kind === VARIANT_KIND_SINGLE_CATCH_ALL) return ALL_SUBSTRATES
    if (kind in KIND_TO_SUBSTRATE) return KIND_TO_SUBSTRATE[kind]
    return undefined
}

/**
 * Is this colorway DECLARED for this colour route -- the ladder's rung 1?
 *
 * The single definition of "route match", used by the ladder below and by
 * the combined card's auto-bind (#687), which binds only an exact match: a
 * rung-2 fallback is a fine Suggested hint but, on a card standing in for a
 * light + dark pair, binding one would be a guess. A missing
 * `substrate_target` reads as NULL, which is how "all colours" is stored.
 * `specific_colors` has no route-implied substrate, so nothing matches it.
 */
export function colorwayMatchesRoute({ kind, colorway } = {}) {
    if (!colorway || typeof colorway !== 'object') return false
    const want = impliedSubstrateForKind(kind)
    if (want === undefined) return false
    return (colorway.substrate_target ?? null) === want
}

/**
 * Slot types whose colour is NOT knowable before a garment is chosen.
 *
 * `match_garment`, `match_garment_or_white` and `contrast_auto` are resolved
 * SERVER-SIDE against one concrete garment colour (see ArtworkColorway's
 * SLOT_TYPES and the `garmentHex` the bind endpoint is sent). Whatever hex is
 * attached to such a slot in `threads_json` is a stored artefact, not a
 * promise about what gets sewn -- so scoring it would manufacture a confident
 * verdict out of a placeholder, which is precisely the defect
 * `artworkReadability`'s docstring exists to warn about.
 *
 * A missing `type` IS `fixed`: bare thread objects predate typed slots and
 * that is exactly what they meant (ArtworkColorway.normalize_slots says so on
 * the read path).
 */
const UNRESOLVED_SLOT_TYPES = Object.freeze(new Set([
    'match_garment',
    'match_garment_or_white',
    'contrast_auto',
]))

/**
 * The hexes a colorway is KNOWN to sew, ignoring garment-dependent slots.
 *
 * @returns {string[]} possibly empty -- an empty result means UNSCORABLE,
 *   which is a different answer from "scores badly" and is treated as such.
 */
function knownInkHexes(colorway) {
    const threads = Array.isArray(colorway?.threads) ? colorway.threads : []
    const hexes = []
    for (const t of threads) {
        if (!t || typeof t !== 'object') continue
        const type = t.type || 'fixed'
        if (UNRESOLVED_SLOT_TYPES.has(type)) continue
        if (typeof t.hex === 'string' && t.hex.trim()) hexes.push(t.hex.trim())
    }
    return hexes
}

/**
 * How well a colorway reads on the routed garments, or null if unknowable.
 *
 * Scored by the WORST thread over the WORST garment colour. A colorway sews
 * ALL of its threads onto ALL of the colours the variant routes to, so the
 * honest score is the least readable combination -- the same rule
 * `readabilityFor` already applies across garment colours, extended across
 * the colorway's own threads. Taking the best thread instead would let one
 * readable stitch vouch for an unreadable design.
 *
 * The WCAG math itself is NOT reimplemented: every ratio comes from
 * `readabilityFor` (#2791), which is the single existing ink-vs-garment
 * contrast check.
 */
function contrastScore(colorway, garmentColors) {
    const inks = knownInkHexes(colorway)
    if (inks.length === 0) return null

    let worst = null
    for (const inkHex of inks) {
        const verdict = readabilityFor({ inkHex, garmentColors })
        // null == genuinely unknowable (unparseable ink, or no garment colour
        // with a usable hex). Skip it rather than letting it score as 0.
        if (verdict === null) continue
        if (worst === null || verdict.ratio < worst) worst = verdict.ratio
    }
    return worst
}

function byId(a, b) {
    return a.id - b.id
}

/**
 * Resolve the colorway a variant should default to.
 *
 * @param {object}   args
 * @param {string}   args.kind       variant kind (the colour route)
 * @param {Array}    args.colorways  saved colorways for the artwork version
 * @param {Array}    [args.garmentColors]  the garment colours this variant
 *   routes to, as `{color_sku, name, hex}`. OPTIONAL: when absent (or when
 *   no candidate can be scored) rung 1 falls back to lowest id, which is the
 *   pre-2026-09-02 behaviour intact.
 * @returns {number|null} the colorway id, or null to use the all-black floor
 */
export function resolveDefaultColorwayId({ kind, colorways, garmentColors } = {}) {
    if (!Array.isArray(colorways) || colorways.length === 0) return null

    // Rows without an id cannot be bound to -- writing `undefined` into
    // colorway_id would be worse than falling through to the floor.
    const usable = colorways
        .filter((c) => c && c.id != null)
        .sort(byId)
    if (usable.length === 0) return null

    // Rung 1: a colorway declared for exactly this route.
    //
    // `undefined` (specific_colors) skips this rung entirely; `null`
    // (all colours) does NOT -- it matches the all-substrates colorways,
    // which is the case a freshly-created placement actually hits.
    const want = impliedSubstrateForKind(kind)
    if (want !== undefined) {
        const matches = usable.filter(
            (colorway) => colorwayMatchesRoute({ kind, colorway }),
        )
        if (matches.length > 0) {
            // RANK BY CONTRAST, id AS THE TIE-BREAK (Jake, 2026-09-02):
            // "the default colorway picked for light garment or dark garment
            // should be the one whose contrast against the substrate is most
            // appropriate."
            //
            // This reorders WITHIN the matching set and never widens it -- a
            // light-declared colorway does not become eligible for a dark
            // variant by scoring well, because the declaration is the route
            // and contrast is only the ranking inside it.
            //
            // Only the two SHADE routes are ranked. `single_catch_all` covers
            // a MIX of light and dark garments, so there is no one substrate
            // to be "most appropriate" against; Jake named light and dark, so
            // the all-colours rung keeps lowest-id rather than being given a
            // meaning he did not ask for.
            //
            // `matches` is already id-sorted (`usable` was sorted above), and
            // the reduce below keeps the FIRST of two equal scores -- which is
            // how the lowest-id tie-break survives without a second sort.
            const rankable = want !== ALL_SUBSTRATES
            if (rankable) {
                let best = null
                let bestScore = null
                for (const c of matches) {
                    const score = contrastScore(c, garmentColors)
                    if (score === null) continue
                    if (bestScore === null || score > bestScore) {
                        best = c
                        bestScore = score
                    }
                }
                if (best) return best.id
            }
            // Nothing scorable (no garment colours, unparseable hexes, or an
            // all-colours route): the original lowest-id rule, unchanged.
            return matches[0].id
        }
    }

    // Rung 2: "otherwise pick the first available colorway" (Jake), of any
    // substrate. Note this DELIBERATELY binds a light-declared colorway to a
    // dark variant when nothing better exists -- Jake chose a visible
    // approximate default over an empty one, and the picker stays fully
    // overridable, so the declaration remains advisory rather than a rule.
    return usable[0].id

    // Rung 3 -- no colorways at all -- is the early return above. Null tells
    // the caller to use the existing all-black _DEFAULT_STITCH_RGB render,
    // which Jake confirmed is the correct floor rather than a bug.
}

// ---------------------------------------------------------------------------
// Split light / dark (Jake, 2026-10-21)
// ---------------------------------------------------------------------------
//
// "If the placement is an embroidery decoration with colorways flagged to be
// applied to light/dark garments, use the same decoration for both light and
// dark, but with the matching colorway."

const SHADE_TO_KIND = Object.freeze({
    light: VARIANT_KIND_CATCH_ALL_LIGHT,
    dark: VARIANT_KIND_CATCH_ALL_DARK,
})

/**
 * Does any colorway declare itself for light or dark garments?
 *
 * The split dialog's embroidery rule keys off this: a decoration whose
 * colorways are flagged per shade is meant to be sewn on BOTH shades, each
 * with its own colorway.
 */
export function colorwaysDeclareShades(colorways) {
    if (!Array.isArray(colorways)) return false
    return colorways.some(
        (c) => c && (c.substrate_target === 'light' || c.substrate_target === 'dark'),
    )
}

/**
 * The colorway a shade route should sew right after a split.
 *
 * A colorway DECLARED for the shade wins, ranked exactly as a variant's
 * default is (`resolveDefaultColorwayId`, so contrast breaks ties the same
 * way everywhere). Otherwise the route keeps `fallbackId` -- Jake's split
 * rule, "copy the light one unless a dark colorway already exists" -- but
 * only when that colorway still belongs to this artwork version. Null means
 * bind nothing: a bind buys a paid Artifi render, so an absent answer stays
 * absent rather than becoming a guess.
 *
 * @param {object} args
 * @param {'light'|'dark'} args.shade
 * @param {Array} args.colorways  the placement's saved colorways
 * @param {Array} [args.garmentColors]  the garment colours of that shade
 * @param {number|null} [args.fallbackId]  what to keep when none is declared
 * @returns {number|null}
 */
export function resolveShadeColorwayId({ shade, colorways, garmentColors, fallbackId } = {}) {
    const kind = SHADE_TO_KIND[shade]
    if (!kind || !Array.isArray(colorways) || colorways.length === 0) return null
    if (colorways.some((c) => c && c.id != null && c.substrate_target === shade)) {
        return resolveDefaultColorwayId({ kind, colorways, garmentColors })
    }
    if (fallbackId == null) return null
    return colorways.some((c) => c && c.id === fallbackId) ? fallbackId : null
}
